import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  Language,
  translations,
  getDirection,
} from './thi-translations';
import { normalizeThiLang } from './thi-lang.util';
import { AuthService } from 'src/app/Auth/service/auth.service';

@Component({
  selector: 'app-thi-landing-header',
  templateUrl: './thi-landing-header.component.html',
  styleUrls: ['./thi-landing-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThiLandingHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lang: Language = 'ar';

  t = translations.ar;
  dir: 'rtl' | 'ltr' = 'rtl';
  isMenuOpen = false;
  showLangMenu = false;
  isScrolled = false;
  loggedIn = false;

  /** Matches Lovable `Header.tsx` — codes only (no native names in the menu). */
  readonly languages: { code: Language; label: string }[] = [
    { code: 'ar', label: 'AR' },
    { code: 'en', label: 'EN' },
    { code: 'he', label: 'HE' },
  ];

  private readonly destroy$ = new Subject<void>();
  private scrollListener!: () => void;
  private clickListener!: (e: MouseEvent) => void;

  constructor(
    public router: Router,
    private translate: TranslateService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.applyLang();
    this.loggedIn = !!localStorage.getItem('token');
    this.authService.isLogin
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.loggedIn = !!v;
        this.cdr.markForCheck();
      });
    this.cdr.markForCheck();

    // Subscribe to navigation events to keep active links in sync.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cdr.markForCheck());

    // Register scroll and click listeners OUTSIDE the Angular zone so they
    // never trigger change detection by themselves.  We only re-enter the
    // zone when the UI actually needs to update.
    this.ngZone.runOutsideAngular(() => {
      this.scrollListener = (): void => {
        const next = window.scrollY > 20;
        if (next !== this.isScrolled) {
          this.ngZone.run(() => {
            this.isScrolled = next;
            this.cdr.markForCheck();
          });
        }
      };

      this.clickListener = (e: MouseEvent): void => {
        if (!this.showLangMenu) {
          return;
        }
        const el = e.target as HTMLElement | null;
        if (!el) {
          return;
        }
        if (el.closest('#thi-lang-trigger') || el.closest('#thi-lang-listbox')) {
          return;
        }
        this.ngZone.run(() => {
          this.showLangMenu = false;
          this.cdr.markForCheck();
        });
      };

      window.addEventListener('scroll', this.scrollListener, { passive: true });
      document.addEventListener('click', this.clickListener as EventListener, true);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('scroll', this.scrollListener);
    document.removeEventListener('click', this.clickListener as EventListener, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.applyLang();
      this.cdr.markForCheck();
    }
  }

  private applyLang(): void {
    this.lang = normalizeThiLang(this.lang as unknown as string);
    this.t = translations[this.lang];
    this.dir = getDirection(this.lang);
  }

  get isHome(): boolean {
    const p = (this.router.url || '').split('?')[0] || '/';
    return p === '/home' || p === '/home/' || p === '/' || p === '';
  }

  /** Same landing targets as `LoginComponent.navigateAfterLogin` */
  get accountDashboardLink(): string[] {
    const raw = localStorage.getItem('user_type');
    let ut: number | undefined;
    try {
      ut = raw != null ? JSON.parse(raw) : undefined;
    } catch {
      ut = undefined;
    }
    if (ut === 100 || ut === 101) {
      return ['/dashboard', 'dashboard'];
    }
    if (ut === 300 || ut === 301) {
      return ['/dashboard', 'office-companies'];
    }
    return ['/dashboard', 'dashboard'];
  }

  get headerSurfaceClass(): string {
    if (this.isScrolled) {
      return 'bg-card/95 backdrop-blur-xl shadow-lg border-b border-border';
    }
    if (!this.isHome) {
      return 'bg-card/95 backdrop-blur-xl';
    }
    return 'bg-transparent';
  }

  changeLang(code: Language): void {
    localStorage.setItem('curentLang', code);
    this.translate.use(code);
    this.showLangMenu = false;
    this.isMenuOpen = false;
    window.location.reload();
  }

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.showLangMenu = !this.showLangMenu;
    this.cdr.markForCheck();
  }
}
